/**
 * Derive a short, human-readable category for a router-level error so the
 * Datadog log message is self-describing (e.g. `...catch: Cloudflare 403`)
 * instead of the generic `React Router client error boundary catch` wrapper.
 *
 * Grouped on `@error.message` in Datadog this lets us tell the real RR7
 * single-fetch bugs (routeId / turbo-stream / chunk load) apart from the bot,
 * network and auth noise at a glance — and alert only on the actionable ones.
 * Most of the wrapper's volume is Cloudflare bot 403s and `Failed to fetch`
 * network errors rather than actionable RR7 failures.
 *
 * Input is the coerced `Error` produced by `toLoggableError` in
 * `entry.client.tsx`, whose `message` is one of:
 *   - `No result found for routeId "routes/_app.articles"` (RR7 single-fetch)
 *   - `Unable to decode turbo-stream response`              (RR7 single-fetch)
 *   - `Failed to fetch` / `Load failed`                     (browser fetch failure)
 *   - `ErrorResponse <status> <statusText>: <body>`         (route loader threw a Response)
 */
export const classifyRouterError = (error: Error): string => {
    const message = error.message;

    // --- Real RR7 single-fetch / routing failures (the actionable ones). ---
    if (message.includes('No result found for routeId')) {
        return 'routeId not found';
    }
    if (message.includes('Unable to decode turbo-stream')) {
        return 'turbo-stream decode';
    }
    if (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('ChunkLoadError') ||
        /Loading chunk \S+ failed/.test(message)
    ) {
        return 'chunk load';
    }

    // --- Browser network failures (mostly benign: aborted navs, flaky net). ---
    // Checked after the dynamic-import variant above so it doesn't shadow it.
    if (message === 'Failed to fetch' || message.startsWith('NetworkError')) {
        return 'Failed to fetch';
    }
    if (message === 'Load failed') {
        return 'Load failed';
    }

    // --- HTTP route errors surfaced as `ErrorResponse <status> ...`. ---
    const status = /^ErrorResponse (\d{3})\b/.exec(message)?.[1];
    if (status) {
        switch (status) {
            case '400':
                return 'SWW 400';
            case '401':
                return 'Please login 401';
            case '403':
                // Cloudflare bot challenge ("Just a moment...") vs a plain 403.
                return message.includes('Just a moment')
                    ? 'Cloudflare 403'
                    : 'Forbidden 403';
            case '404':
                return 'Not found 404';
            case '410':
                return 'Gone 410';
            case '500':
                return 'Server error 500';
            case '502':
                return 'Bad gateway 502';
            case '503':
                return 'Service unavailable 503';
            case '504':
                return 'Gateway timeout 504';
            default:
                return `HTTP ${status}`;
        }
    }

    return 'unclassified';
};
